import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtAmountTr, CUR_LABEL, trFold } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Banknote, Search, CheckCircle2, Circle, Wallet, CalendarClock, FileSpreadsheet } from "lucide-react";
import { ZoneFilterButtons } from "@/pages/ProfitAnalysisPage";

const INSTALLMENTS = [
  { key: "pesin", label: "Peşin", pct: 0.25, sub: "%25" },
  { key: "kasim", label: "15 Kasım (Çek)", pct: 0.25, sub: "%25" },
  { key: "aralik", label: "15 Aralık", pct: 0.5, sub: "%50" },
];

const zoneCls = {
  "Blue Zone": "bg-blue-500/10 text-blue-600 border-blue-500/20",
  "Green Zone": "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
};

const isPaid = (v) => v === true || !!v?.paid;
const entryOf = (v) => (v && typeof v === "object" ? v : {});
const fmtDate = (d) => (d ? d.split("-").reverse().join(".") : "");
const curTotalsStr = (totals) =>
  ["TRY", "USD", "EUR"].filter((c) => totals[c]).map((c) => `${fmtAmountTr(totals[c])} ${CUR_LABEL[c]}`).join(" · ") || "0,00 TL";

export default function PaymentPlanPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [q, setQ] = useState("");
  const [zoneSel, setZoneSel] = useState("all");
  const [dlg, setDlg] = useState(null);
  const [dlgDate, setDlgDate] = useState("");
  const [dlgNote, setDlgNote] = useState("");
  const [busy, setBusy] = useState(false);
  const canProdPrice = ["username", "contact_email", "email"].some((k) => (user?.[k] || "").toLowerCase() === "muratbucak@quattrostand.com");

  useEffect(() => {
    if (!canProdPrice) { navigate("/atayici"); return; }
    api.get("/assigner/payment-plan").then((r) => { setRows(r.data); setLoaded(true); }).catch((e) => { toast.error(apiError(e)); setLoaded(true); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDlg = (p, insKey) => {
    const e = entryOf(p.payment_plan_paid?.[insKey]);
    setDlgDate(e.date || new Date().toISOString().slice(0, 10));
    setDlgNote(e.note || "");
    setDlg({ p, insKey });
  };

  const savePaid = async (paid) => {
    const { p, insKey } = dlg;
    setBusy(true);
    try {
      const r = await api.patch(`/assigner/payment-plan/${p.id}`, { installment: insKey, paid, date: paid ? dlgDate || null : null, note: paid ? dlgNote : null });
      setRows((rs) => rs.map((x) => (x.id === p.id ? { ...x, payment_plan_paid: { ...x.payment_plan_paid, [insKey]: r.data.entry } } : x)));
      toast.success(paid ? "Ödendi olarak kaydedildi" : "Ödeme geri alındı");
      setDlg(null);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const filtered = useMemo(() => {
    const f = trFold(q.trim());
    let list = zoneSel === "all" ? rows : rows.filter((p) => p.zone === zoneSel);
    if (f) list = list.filter((p) => trFold(`${p.name} ${p.producer_name} ${p.stand_no || ""}`).includes(f));
    return list;
  }, [rows, q, zoneSel]);

  const byProducer = useMemo(() => {
    const m = new Map();
    filtered.forEach((p) => {
      const key = p.producer_name;
      if (!m.has(key)) m.set(key, { name: key, count: 0, totals: {}, inst: { pesin: {}, kasim: {}, aralik: {} }, paid: { pesin: {}, kasim: {}, aralik: {} } });
      const g = m.get(key);
      const cur = p.producer_price_currency || "TRY";
      const amt = Number(p.producer_price) || 0;
      g.count += 1;
      g.totals[cur] = (g.totals[cur] || 0) + amt;
      INSTALLMENTS.forEach((ins) => {
        g.inst[ins.key][cur] = (g.inst[ins.key][cur] || 0) + amt * ins.pct;
        if (isPaid(p.payment_plan_paid?.[ins.key])) g.paid[ins.key][cur] = (g.paid[ins.key][cur] || 0) + amt * ins.pct;
      });
    });
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name, "tr"));
  }, [filtered]);

  const grand = useMemo(() => {
    const g = { totals: {}, inst: { pesin: {}, kasim: {}, aralik: {} }, paid: { pesin: {}, kasim: {}, aralik: {} } };
    filtered.forEach((p) => {
      const cur = p.producer_price_currency || "TRY";
      const amt = Number(p.producer_price) || 0;
      g.totals[cur] = (g.totals[cur] || 0) + amt;
      INSTALLMENTS.forEach((ins) => {
        g.inst[ins.key][cur] = (g.inst[ins.key][cur] || 0) + amt * ins.pct;
        if (isPaid(p.payment_plan_paid?.[ins.key])) g.paid[ins.key][cur] = (g.paid[ins.key][cur] || 0) + amt * ins.pct;
      });
    });
    return g;
  }, [filtered]);

  const exportExcel = async () => {
    const XLSX = await import("xlsx");
    const data = filtered.map((p, i) => {
      const cur = CUR_LABEL[p.producer_price_currency || "TRY"];
      const amt = Number(p.producer_price) || 0;
      const row = {
        "#": i + 1,
        "Proje": p.name || "",
        "Bölge": p.zone || "",
        "Stand No": p.stand_no || "",
        "Üretici": p.producer_name || "",
        "Toplam": `${fmtAmountTr(amt)} ${cur}`,
      };
      INSTALLMENTS.forEach((ins) => {
        const v = p.payment_plan_paid?.[ins.key];
        const e = entryOf(v);
        row[`${ins.label} ${ins.sub}`] = `${fmtAmountTr(amt * ins.pct)} ${cur}`;
        row[`${ins.label} Durum`] = isPaid(v)
          ? ["Ödendi", e.date ? fmtDate(e.date) : "", e.by || "", e.note || ""].filter(Boolean).join(" · ")
          : "Ödenmedi";
      });
      return row;
    });
    const totalRow = { "#": "", "Proje": `Genel Toplam (${filtered.length} proje)`, "Bölge": "", "Stand No": "", "Üretici": "", "Toplam": curTotalsStr(grand.totals) };
    INSTALLMENTS.forEach((ins) => {
      totalRow[`${ins.label} ${ins.sub}`] = curTotalsStr(grand.inst[ins.key]);
      totalRow[`${ins.label} Durum`] = Object.keys(grand.paid[ins.key]).length ? `Ödenen: ${curTotalsStr(grand.paid[ins.key])}` : "";
    });
    data.push(totalRow);
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = [{ wch: 4 }, { wch: 40 }, { wch: 12 }, { wch: 10 }, { wch: 24 }, { wch: 18 }, { wch: 18 }, { wch: 34 }, { wch: 18 }, { wch: 34 }, { wch: 18 }, { wch: 34 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ödeme Planı");
    XLSX.writeFile(wb, `odeme-plani-${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success(`${filtered.length} proje Excel'e aktarıldı`);
  };

  const PaidToggle = ({ p, insKey }) => {
    const v = p.payment_plan_paid?.[insKey];
    const paid = isPaid(v);
    const e = entryOf(v);
    return (
      <div className="flex flex-col items-end gap-0.5">
        <button
          type="button"
          onClick={() => openDlg(p, insKey)}
          data-testid={`pp-paid-${insKey}-${p.id}`}
          title={paid ? "Ödeme detayını düzenle / geri al" : "Ödendi işaretle"}
          className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 border transition-colors duration-150 ${
            paid ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-white border-slate-200 text-slate-400 hover:border-slate-400 hover:text-slate-600"
          }`}
        >
          {paid ? <CheckCircle2 size={11} /> : <Circle size={11} />}
          {paid ? "Ödendi" : "Ödenmedi"}
        </button>
        {paid && (e.date || e.by) && (
          <div className="text-[10px] text-slate-400 text-right leading-tight" data-testid={`pp-meta-${insKey}-${p.id}`}>
            {[fmtDate(e.date), e.by].filter(Boolean).join(" · ")}
            {e.note && <div className="italic text-slate-400 max-w-[180px] truncate" title={e.note}>{e.note}</div>}
          </div>
        )}
      </div>
    );
  };

  const dlgPaid = dlg ? isPaid(dlg.p.payment_plan_paid?.[dlg.insKey]) : false;
  const dlgIns = dlg ? INSTALLMENTS.find((i) => i.key === dlg.insKey) : null;

  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="payment-plan-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-heading text-xl font-extrabold tracking-tight">Gett<span className="text-slate-400">Proposal</span></div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold flex items-center gap-1"><Wallet size={10} /> Ödeme Planı</div>
          </div>
          <button
            onClick={() => navigate("/atayici")}
            data-testid="payment-plan-back"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-900 px-3 py-2 transition-colors duration-150"
          >
            <ArrowLeft size={14} /> Geri Dön
          </button>
        </div>
      </header>
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight">Ödeme Planı</h1>
            <p className="text-sm text-slate-500 mt-1">
              Üretici fiyatı girilmiş {loaded ? filtered.length : "…"} proje — %25 peşin · %25 15 Kasım (çek) · %50 15 Aralık
            </p>
            <div className="mt-4">
              <ZoneFilterButtons zones={user?.zones || []} value={zoneSel} onChange={setZoneSel} testidPrefix="pp" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Proje, üretici ara..." data-testid="payment-plan-search" className="rounded-none border-slate-200 h-10 pl-9 w-64 bg-white" />
            </div>
            <Button variant="outline" onClick={exportExcel} data-testid="payment-plan-export-excel" className="rounded-none h-10 border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold gap-2 transition-colors duration-200">
              <FileSpreadsheet size={15} /> Excel
            </Button>
          </div>
        </div>

        <section className="space-y-3" data-testid="payment-plan-producer-summary">
          <h2 className="text-xs uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1.5"><Banknote size={13} /> Üretici Bazında Özet</h2>
          <div className="bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-400">
                  <th className="text-left px-4 py-3 font-semibold">Üretici</th>
                  <th className="text-right px-4 py-3 font-semibold">Proje</th>
                  <th className="text-right px-4 py-3 font-semibold">Toplam</th>
                  {INSTALLMENTS.map((ins) => (
                    <th key={ins.key} className="text-right px-4 py-3 font-semibold">{ins.label} <span className="text-slate-300">{ins.sub}</span></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {byProducer.map((g) => (
                  <tr key={g.name} className="border-b border-slate-100 last:border-0" data-testid={`pp-producer-row-${g.name}`}>
                    <td className="px-4 py-3 font-semibold text-slate-800">{g.name}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-500">{g.count}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold">{curTotalsStr(g.totals)}</td>
                    {INSTALLMENTS.map((ins) => (
                      <td key={ins.key} className="px-4 py-3 text-right tabular-nums">
                        <div>{curTotalsStr(g.inst[ins.key])}</div>
                        {Object.keys(g.paid[ins.key]).length > 0 && (
                          <div className="text-[10px] text-emerald-600 mt-0.5">Ödenen: {curTotalsStr(g.paid[ins.key])}</div>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                {loaded && byProducer.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Üretici fiyatı girilmiş proje bulunamadı.</td></tr>
                )}
              </tbody>
              {byProducer.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-slate-900 bg-slate-50 font-semibold" data-testid="pp-producer-total-row">
                    <td className="px-4 py-3">Genel Toplam</td>
                    <td className="px-4 py-3 text-right tabular-nums">{filtered.length}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{curTotalsStr(grand.totals)}</td>
                    {INSTALLMENTS.map((ins) => (
                      <td key={ins.key} className="px-4 py-3 text-right tabular-nums">
                        <div>{curTotalsStr(grand.inst[ins.key])}</div>
                        {Object.keys(grand.paid[ins.key]).length > 0 && (
                          <div className="text-[10px] text-emerald-600 mt-0.5 font-normal">Ödenen: {curTotalsStr(grand.paid[ins.key])}</div>
                        )}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </section>

        <section className="space-y-3" data-testid="payment-plan-detail">
          <h2 className="text-xs uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1.5"><CalendarClock size={13} /> Proje Detayı</h2>
          <div className="bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-400">
                  <th className="text-left px-4 py-3 font-semibold">Proje</th>
                  <th className="text-left px-4 py-3 font-semibold">Üretici</th>
                  <th className="text-right px-4 py-3 font-semibold">Toplam</th>
                  {INSTALLMENTS.map((ins) => (
                    <th key={ins.key} className="text-right px-4 py-3 font-semibold">{ins.label} <span className="text-slate-300">{ins.sub}</span></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const cur = CUR_LABEL[p.producer_price_currency || "TRY"];
                  const amt = Number(p.producer_price) || 0;
                  return (
                    <tr key={p.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors duration-100" data-testid={`pp-row-${p.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-800">{p.name}</div>
                        <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-slate-400">
                          {p.zone && <span className={`px-1.5 py-0.5 border ${zoneCls[p.zone] || "border-slate-200"}`}>{p.zone}</span>}
                          {p.stand_no && <span>Stand: {p.stand_no}</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{p.producer_name}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold whitespace-nowrap">{fmtAmountTr(amt)} {cur}</td>
                      {INSTALLMENTS.map((ins) => (
                        <td key={ins.key} className="px-4 py-3 text-right whitespace-nowrap">
                          <div className="tabular-nums">{fmtAmountTr(amt * ins.pct)} {cur}</div>
                          <div className="mt-1 flex justify-end"><PaidToggle p={p} insKey={ins.key} /></div>
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {loaded && filtered.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Üretici fiyatı girilmiş proje bulunamadı.</td></tr>
                )}
              </tbody>
              {filtered.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-slate-900 bg-slate-50 font-semibold" data-testid="pp-detail-total-row">
                    <td className="px-4 py-3" colSpan={2}>Genel Toplam ({filtered.length} proje)</td>
                    <td className="px-4 py-3 text-right tabular-nums">{curTotalsStr(grand.totals)}</td>
                    {INSTALLMENTS.map((ins) => (
                      <td key={ins.key} className="px-4 py-3 text-right tabular-nums">{curTotalsStr(grand.inst[ins.key])}</td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </section>
      </main>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="rounded-none sm:max-w-md" data-testid="pp-pay-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {dlg?.p?.name} — {dlgIns?.label} <span className="text-slate-400 font-normal">{dlgIns?.sub}</span>
            </DialogTitle>
          </DialogHeader>
          {dlg && (
            <div className="space-y-4">
              <div className="text-sm text-slate-500">
                Taksit tutarı:{" "}
                <span className="font-semibold text-slate-900 tabular-nums">
                  {fmtAmountTr((Number(dlg.p.producer_price) || 0) * dlgIns.pct)} {CUR_LABEL[dlg.p.producer_price_currency || "TRY"]}
                </span>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-500">Ödeme Tarihi</Label>
                <Input type="date" value={dlgDate} onChange={(e) => setDlgDate(e.target.value)} data-testid="pp-pay-date" className="rounded-none border-slate-200 h-10" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-500">Not (opsiyonel)</Label>
                <Textarea value={dlgNote} onChange={(e) => setDlgNote(e.target.value)} placeholder="Örn: Çek no, banka, açıklama..." data-testid="pp-pay-note" className="rounded-none border-slate-200 min-h-[70px]" />
              </div>
              {dlgPaid && entryOf(dlg.p.payment_plan_paid?.[dlg.insKey]).by && (
                <div className="text-xs text-slate-400">
                  Son işaretleyen: {entryOf(dlg.p.payment_plan_paid?.[dlg.insKey]).by}
                </div>
              )}
              <div className="flex items-center justify-between gap-2 pt-1">
                {dlgPaid ? (
                  <Button variant="outline" disabled={busy} onClick={() => savePaid(false)} data-testid="pp-pay-undo" className="rounded-none h-10 border-red-200 text-red-600 hover:bg-red-50 font-semibold">
                    Ödemeyi Geri Al
                  </Button>
                ) : <span />}
                <Button disabled={busy} onClick={() => savePaid(true)} data-testid="pp-pay-save" className="rounded-none h-10 bg-slate-900 text-white hover:bg-slate-700 font-semibold">
                  {dlgPaid ? "Güncelle" : "Ödendi Olarak Kaydet"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
